// src/frontend_react/src/pages/UserDashboard/JobManagementPage.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Base UI Components
import { FiLayout, FiPlus, FiEye } from 'react-icons/fi';
import Alert from '../../components/ui/Alert/Alert';
import Button from '../../components/ui/Button/Button';
import Card from '../../components/ui/Card/Card';
import Icon from '../../components/ui/Icon/Icon';
import PromptModal from '../../components/shared/PromptModal';
import Select from '../../components/ui/Select/Select';
import Spinner from '../../components/ui/Spinner/Spinner';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Table from '../../components/ui/Table/Table';
import Typography from '../../components/ui/Typography/Typography';
import Box from '../../components/ui/Box/Box';
import Badge from '../../components/ui/Badge/Badge';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import Tooltip from '../../components/ui/Tooltip/Tooltip';

// Services, Context, and Child Components
import * as api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { notify } from '../../services/notificationService';
import JobDetailPane from './JobDetailPane';
import styles from './JobManagementPage.module.css';

function JobManagementPage() {
  const navigate = useNavigate();
  const { batchId } = useParams();
  const queryClient = useQueryClient();
  const { hasPermission, getImpliedSourceType } = useAuth();
  
  const { 
    selectedProjectId, 
    handleProjectChange, 
    projects,
    isLoading: isLoadingProjects, 
    error: projectsError 
  } = useOutletContext();
  
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState(null);

  const canCreateJob = hasPermission('batch:create');
  const stewardSourceType = getImpliedSourceType();
  const isStewardView = stewardSourceType && !canCreateJob;

  const {
    data: jobs = [],
    isLoading: isLoadingJobs,
    error: jobsError,
  } = useQuery({
    queryKey: ['jobs', selectedProjectId],
    queryFn: () => api.listBatches(selectedProjectId),
    enabled: !!selectedProjectId,
  });

  const createJobMutation = useMutation({
    mutationFn: (jobName) => api.createBatch(selectedProjectId, jobName || undefined),
    onSuccess: (newJob) => {
      queryClient.invalidateQueries({ queryKey: ['jobs', selectedProjectId] }).then(() => {
        if (newJob?.id) {
          navigate(`/user/workspace/${newJob.id}/overview`);
        }
      });
      notify.success("New Reconciliation Job created successfully.");
    },
    onError: (err) => {
      notify.error(err.message || "Failed to create new job.");
    },
    onSettled: () => {
      setIsPromptModalOpen(false);
    }
  });

  useEffect(() => {
    setExpandedJobId(batchId ? parseInt(batchId, 10) : null);
  }, [batchId]);

  const handleCreateJobFromModal = (jobName) => {
    createJobMutation.mutate(jobName);
  };

  const handleViewJob = useCallback((job) => {
    if (createJobMutation.isLoading) return;
    const newExpandedId = expandedJobId === job.id ? null : job.id;
    navigate(newExpandedId ? `/user/workspace/${newExpandedId}/overview` : '/user/workspace');
  }, [expandedJobId, createJobMutation.isLoading, navigate]);
  
  const filteredJobs = useMemo(() => {
    if (!isStewardView) return jobs;
    return jobs.filter(job => 
        job.status === 'in_progress' && 
        job.progress?.total_dst_names?.includes(stewardSourceType)
    );
  }, [jobs, isStewardView, stewardSourceType]);

  const columns = useMemo(() => [
    { Header: 'Job ID', accessor: 'id', width: 100 },
    { Header: 'Name / Description', accessor: 'name', Cell: ({ value }) => value || <Typography variant="caption" color="placeholder">Untitled Job</Typography> },
    { Header: 'Status', accessor: 'status', Cell: ({ value }) => <Badge variant={value === 'completed' ? 'success' : (value === 'in_progress' ? 'info' : 'default')}>{value.replace(/_/g, ' ')}</Badge> },
    { Header: 'Progress', accessor: 'progress', Cell: ({ value }) => `${value?.submitted || 0} of ${value?.total || 0} Submitted` },
    { Header: 'Created At', accessor: 'created_at', Cell: ({ value }) => new Date(value).toLocaleString() },
    {
      Header: 'Actions',
      id: 'actions',
      Cell: ({ row }) => (
        <Button variant="primary" size="sm" onClick={() => handleViewJob(row)} leftIcon={<Icon as={FiEye} />} aria-label={`View job ${row.id}`}>
          {expandedJobId === row.id ? 'Close' : 'View'}
        </Button>
      )
    }
  ], [expandedJobId, handleViewJob]);

  const HeaderActions = () => {
    if (!canCreateJob) return null;
    
    const button = (
      <Button
        variant="primary"
        onClick={() => setIsPromptModalOpen(true)}
        leftIcon={<Icon as={FiPlus} />}
        disabled={!selectedProjectId || createJobMutation.isLoading}
      >
        Start New Reconciliation Job
      </Button>
    );

    return !selectedProjectId ? (
      <Tooltip content="Please select a project first to start a new job.">
        <span>{button}</span>
      </Tooltip>
    ) : button;
  };

  const WorkspaceHeader = () => (
    <Card>
      <Stack p="xl" spacing="xl">
        {/* Title and Subtitle */}
        <Stack spacing="xs">
          <HStack spacing="md" alignItems="center">
            <Icon as={FiLayout} size="h2" color="primary" />
            <Typography variant="h3" component="h1" m={0}>
              Project Workspace
            </Typography>
          </HStack>
          <Typography color="secondary" pl="lg">
            Select a project to view and manage its reconciliation jobs.
          </Typography>
        </Stack>

        {/* Button aligned right */}
        <Box display="flex" justifyContent="flex-end">
          <HeaderActions />
        </Box>

        {/* Project Selector */}
        <Box>
          <Select
            label="Active Project"
            labelSrOnly
            value={selectedProjectId}
            onChange={(e) => handleProjectChange(e.target.value)}
            options={[{ value: '', label: '-- Select a Project --' }, ...(projects || []).map(p => ({ value: p.id, label: p.name }))]}
            disabled={isLoadingProjects || createJobMutation.isLoading}
          />
        </Box>
      </Stack>
    </Card>
  );


  const emptyStateMessage = isStewardView 
    ? "There are no active jobs requiring your data contribution for this project." 
    : "There are no reconciliation jobs for this project. Use the button above to start the first one.";

  if (isLoadingProjects) {
    return <Box display="flex" justifyContent="center" py="xl"><Spinner size="lg" /><Typography ml={2}>Loading projects...</Typography></Box>;
  }
  
  return (
    <Stack spacing="xl" className={styles.workspaceContainer}>
      {(projectsError || jobsError) && <Alert variant="error">{projectsError || jobsError.message}</Alert>}
      <WorkspaceHeader />
  
      
      {!selectedProjectId ? (
        <EmptyState
            title="No Project Selected"
            message="Please select a project from the dropdown above to view its reconciliation jobs."
            icon={FiLayout}
        />
      ) : (
        <div className={styles.tableWrapper}>
          <Table
            columns={columns}
            data={filteredJobs}
            isLoading={isLoadingJobs}
            emptyStateTitle="No Reconciliation Jobs Found"
            emptyMessage={emptyStateMessage}
            emptyStateIcon={FiLayout}
          >
            {({ row, children, rowIndex }) => (
              <React.Fragment key={row.id || rowIndex}>
                <tr className={`${styles.masterRow} ${expandedJobId === row.id ? styles.expanded : ''}`}>
                  {children}
                </tr>
                {expandedJobId === row.id && (
                  <tr className={styles.detailRow}>
                    <td colSpan={columns.length}>
                      <JobDetailPane />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )}
          </Table>
        </div>
      )}

      <PromptModal
        isOpen={isPromptModalOpen}
        onClose={() => !createJobMutation.isLoading && setIsPromptModalOpen(false)}
        onSubmit={handleCreateJobFromModal} 
        title="Start a New Reconciliation Job"
        inputLabel="Job Name (Optional)"
        submitText="Create Job"
        isSubmitting={createJobMutation.isLoading}
      />
    </Stack>
  );
}

export default JobManagementPage;